import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { 
  Plus, 
  User,
  MagnifyingGlass,
  PencilSimple,
  Envelope,
  Phone,
  Trash,
  Shield,
  House,
  Crown,
  Users as UsersIcon,
  UserCirclePlus,
  CheckCircle,
  XCircle,
  Key,
  MapPin,
  CaretUp,
  CaretDown,
  CaretUpDown,
  FilePdf,
  Clock
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { createUser, updateUser, deleteUser, invalidateCommHubUsersCache } from '../lib/api';
import { useDashboard, useApartments, usePendingUpdates, useInvalidateQueries } from '../hooks/useApiQueries';
import type { Icon } from '@phosphor-icons/react';
import PendingMemberUpdates from '../components/PendingMemberUpdates';

// Type definitions
interface RoleConfig {
  level: number;
  label: string;
  color: string;
  icon: React.ElementType;
}

interface RolesMap {
  [key: string]: RoleConfig;
}

interface UserData {
  id: string;
  name: string;
  first_name?: string;
  last_name?: string;
  email: string;
  phone?: string;
  personal_number?: string;
  role: string;
  apartment_id?: string;
  office_location?: string;
  is_active?: boolean;
  is_invoice_recipient?: boolean;
  organization_id?: string;
}

interface ApartmentData {
  id: string;
  apartment_number?: string;
  property_id?: string;
  residents?: Array<{ id: string; name?: string }>;
}

// Default role configurations (fallback for known roles)
const DEFAULT_ROLE_CONFIG: RolesMap = {
  superadmin: { level: 100, label: 'Superadmin', color: 'bg-purple-100 text-purple-800', icon: Crown },
  admin: { level: 80, label: 'Admin', color: 'bg-red-100 text-red-800', icon: Shield },
  board_member: { level: 60, label: 'Styrelsemedlem', color: 'bg-amber-100 text-amber-800', icon: UsersIcon },
  auditor: { level: 55, label: 'Revisor', color: 'bg-blue-100 text-blue-800', icon: Shield },
  suppliant: { level: 50, label: 'Suppleant', color: 'bg-indigo-100 text-indigo-800', icon: UsersIcon },
  resident: { level: 20, label: 'Boende', color: 'bg-slate-100 text-slate-800', icon: User },
  subcontractor: { level: 10, label: 'Leverantör', color: 'bg-green-100 text-green-800', icon: House },
  external: { level: 7, label: 'Extern', color: 'bg-sky-100 text-sky-800', icon: User }
};

// Color palette for dynamically discovered roles
const DYNAMIC_ROLE_COLORS = [
  'bg-teal-100 text-teal-800',
  'bg-orange-100 text-orange-800',
  'bg-pink-100 text-pink-800',
  'bg-cyan-100 text-cyan-800',
  'bg-lime-100 text-lime-800',
  'bg-rose-100 text-rose-800',
];

// Function to get role config (handles dynamic roles)
const getRoleConfig = (role: string | undefined, allRoles: RolesMap): RoleConfig => {
  if (!role) return DEFAULT_ROLE_CONFIG.resident;
  if (allRoles[role]) return allRoles[role];
  if (DEFAULT_ROLE_CONFIG[role]) return DEFAULT_ROLE_CONFIG[role];
  
  // Generate config for unknown role
  const colorIndex = role.charCodeAt(0) % DYNAMIC_ROLE_COLORS.length;
  return {
    level: 30,
    label: role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, ' '),
    color: DYNAMIC_ROLE_COLORS[colorIndex],
    icon: User
  };
};

type SortField = 'name' | 'role' | 'apartment' | 'status';
type SortDirection = 'asc' | 'desc';

export default function UsersPage() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const { invalidateUsers } = useInvalidateQueries();
  
  // React Query hooks - always fetch, filter locally if needed
  const { data: dashboardData, isLoading: dashboardLoading, refetch: refetchDashboard } = useDashboard(
    currentUser?.organization_id,
    true
  );
  const { data: apartmentsData = [], refetch: refetchApartments } = useApartments(
    currentUser?.organization_id,
    true
  );
  const { data: pendingUpdatesData = [], refetch: refetchPending } = usePendingUpdates(
    currentUser?.organization_id,
    !!currentUser?.organization_id // Pending updates requires org_id
  );
  
  // Extract users from dashboard data
  const allUsers: UserData[] = (dashboardData?.users || []) as UserData[];
  const users = currentUser?.role !== 'superadmin' && currentUser?.organization_id
    ? allUsers.filter(u => u.organization_id === currentUser.organization_id)
    : allUsers;
    
  // Extract apartments
  const apartments: ApartmentData[] = apartmentsData as ApartmentData[];
  const pendingCount = pendingUpdatesData.length;
  const loading = dashboardLoading;
  
  // Build dynamic roles from users - always include default roles
  const ROLES = (() => {
    const dynamicRoles: RolesMap = { ...DEFAULT_ROLE_CONFIG };
    users.forEach(u => {
      if (u.role && !dynamicRoles[u.role]) {
        dynamicRoles[u.role] = getRoleConfig(u.role, dynamicRoles);
      }
    });
    return dynamicRoles;
  })();
  
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState<UserData | null>(null);
  const [userToDelete, setUserToDelete] = useState<UserData | null>(null);
  const [activeTab, setActiveTab] = useState('users');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    personal_number: '',
    role: 'resident',
    apartment_id: '',
    office_location: '',
    is_active: true,
    is_invoice_recipient: false
  });
  
  // Refetch helper
  const fetchData = useCallback(() => {
    invalidateUsers();
    refetchDashboard();
    refetchApartments();
    refetchPending();
  }, [invalidateUsers, refetchDashboard, refetchApartments, refetchPending]);

  // Check if current user can manage this role
  const canManageRole = (role: string) => {
    const currentLevel = ROLES[currentUser?.role || '']?.level || 0;
    const targetLevel = ROLES[role]?.level || 0;
    return currentLevel > targetLevel;
  };

  // Check if current user can edit another user (higher level and not self)
  const canEditUser = (user: UserData) => {
    if (user.id === currentUser?.id) return false; // Can't edit self
    return canManageRole(user.role);
  };

  // Get available roles for current user to assign
  const getAssignableRoles = (): string[] => {
    const currentLevel = ROLES[currentUser?.role || '']?.level || 0;
    // Allow assigning roles at same level or lower (except superadmin which requires higher)
    return Object.entries(ROLES)
      .filter(([key, config]) => {
        // Superadmin can only be assigned by superadmin
        if (key === 'superadmin') return currentLevel >= 100;
        // Allow same level or lower
        return config.level <= currentLevel;
      })
      .map(([key]) => key);
  };

  // Filter users locally
  const filteredUsers = users.filter(u => {
    const matchesSearch = !search || 
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.phone?.includes(search);
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  // Helper functions for sorting (must be defined before sortedUsers)
  const getApartmentForUser = (userId: string, userApartmentId: string | undefined): ApartmentData | null => {
    // Strategy 1: Match by user.apartment_id against apartment.id or apartment_number
    if (userApartmentId) {
      const apt = apartments.find(a => a.id === userApartmentId || a.apartment_number === userApartmentId);
      if (apt) return apt;
    }
    
    // Strategy 2: Reverse lookup - find apartment that has this user in its residents array
    const aptWithResident = apartments.find(apt => 
      apt.residents?.some(r => r.id === userId)
    );
    if (aptWithResident) return aptWithResident;
    
    return null;
  };

  const getApartmentLabel = (userId: string, apartmentId: string | undefined): string | null => {
    const apt = getApartmentForUser(userId, apartmentId);
    return apt ? `Lgh ${apt.apartment_number}` : null;
  };

  // Sort users
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <CaretUpDown size={14} className="text-slate-400" />;
    return sortDirection === 'asc' 
      ? <CaretUp size={14} className="text-primary" weight="bold" />
      : <CaretDown size={14} className="text-primary" weight="bold" />;
  };

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    
    switch (sortField) {
      case 'name':
        return direction * (a.name || '').localeCompare(b.name || '', 'sv');
      case 'role':
        const roleA = ROLES[a.role]?.level || 0;
        const roleB = ROLES[b.role]?.level || 0;
        return direction * (roleB - roleA);
      case 'apartment':
        const aptA = getApartmentLabel(a.id, a.apartment_id) || '';
        const aptB = getApartmentLabel(b.id, b.apartment_id) || '';
        return direction * aptA.localeCompare(aptB, 'sv', { numeric: true });
      case 'status':
        const statusA = a.is_active !== false ? 1 : 0;
        const statusB = b.is_active !== false ? 1 : 0;
        return direction * (statusB - statusA);
      default:
        return 0;
    }
  });

  const handleOpenDialog = (user: UserData | null = null) => {
    if (user) {
      setEditUser(user);
      // Use reverse lookup to find apartment if user.apartment_id is empty
      const userApartment = getApartmentForUser(user.id, user.apartment_id);
      // Split name into first/last if not already available
      const nameParts = (user.name || '').split(' ');
      const firstName = user.first_name || nameParts[0] || '';
      const lastName = user.last_name || nameParts.slice(1).join(' ') || '';
      setFormData({
        first_name: firstName,
        last_name: lastName,
        email: user.email || '',
        phone: user.phone || '',
        personal_number: user.personal_number || '',
        role: user.role || 'resident',
        apartment_id: userApartment?.id || user.apartment_id || '',
        office_location: user.office_location || '',
        is_active: user.is_active !== false,
        is_invoice_recipient: user.is_invoice_recipient || false
      });
    } else {
      setEditUser(null);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        personal_number: '',
        role: 'resident',
        apartment_id: '',
        office_location: '',
        is_active: true,
        is_invoice_recipient: false
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.first_name || !formData.email) {
      toast.error('Fyll i förnamn och e-post');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Ogiltig e-postadress');
      return;
    }
    
    try {
      // Combine first_name and last_name to name for backwards compatibility
      const fullName = `${formData.first_name} ${formData.last_name || ''}`.trim();
      
      const userData: Record<string, unknown> = {
        name: fullName,
        first_name: formData.first_name,
        last_name: formData.last_name || '',
        email: formData.email,
        phone: formData.phone,
        personal_number: formData.personal_number,
        role: formData.role,
        apartment_id: formData.apartment_id,
        office_location: formData.office_location,
        is_active: formData.is_active,
        is_invoice_recipient: formData.is_invoice_recipient,
        organization_id: currentUser?.organization_id,
        updated_at: new Date().toISOString()
      };

      console.log('Saving user with organization_id:', currentUser?.organization_id);
      console.log('Full userData:', userData);

      if (editUser) {
        await updateUser(editUser.id, userData);
        toast.success('Användare uppdaterad');
      } else {
        userData.id = crypto.randomUUID();
        userData.created_at = new Date().toISOString();
        const result = await createUser(userData);
        console.log('Create user result:', result);
        toast.success('Användare skapad');
      }
      
      invalidateCommHubUsersCache();
      setDialogOpen(false);
      
      // Small delay to ensure cache is invalidated before fetching
      setTimeout(() => fetchData(), 500);
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara');
    }
  };

  const handleDeleteClick = (user: UserData) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    
    try {
      await deleteUser(userToDelete.id);
      toast.success('Användare borttagen');
      invalidateCommHubUsersCache();
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Kunde inte ta bort användare');
    }
  };

  // Quick role change directly from list
  const handleQuickRoleChange = async (user: UserData, newRole: string) => {
    if (user.id === currentUser?.id) {
      toast.error('Du kan inte ändra din egen roll');
      return;
    }
    
    try {
      await updateUser(user.id, { role: newRole as 'superadmin' | 'admin' | 'auditor' | 'suppliant' | 'board_member' | 'resident' | 'supplier' | 'external' });
      toast.success(`${user.name || user.email} är nu ${ROLES[newRole]?.label}`);
      invalidateCommHubUsersCache();
      fetchData();
    } catch (error) {
      console.error('Role change error:', error);
      toast.error('Kunde inte ändra roll');
    }
  };

  const RoleBadge = ({ role }: { role: string | undefined }) => {
    const config = getRoleConfig(role, ROLES);
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon size={12} weight="bold" />
        {config.label}
      </span>
    );
  };

  const StatusBadge = ({ isActive }: { isActive: boolean | undefined }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
      isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
    }`}>
      {isActive ? <CheckCircle size={12} /> : <XCircle size={12} />}
      {isActive ? 'Aktiv' : 'Inaktiv'}
    </span>
  );

  // Export users to PDF
  const handleExportPDF = () => {
    // Only include users with apartments
    const usersWithApartments = users.filter(user => {
      const apt = getApartmentForUser(user.id, user.apartment_id);
      return apt !== null;
    });
    
    // Sort by apartment number (1A, 1B, 2A, 2B, etc.)
    const sortedUsers = [...usersWithApartments].sort((a, b) => {
      const aptA = getApartmentForUser(a.id, a.apartment_id)?.apartment_number || '';
      const aptB = getApartmentForUser(b.id, b.apartment_id)?.apartment_number || '';
      return aptA.localeCompare(aptB, 'sv', { numeric: true });
    });
    
    console.log('Exporting PDF - Users with apartments:', sortedUsers.length);
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Medlemsregister', pageWidth / 2, 20, { align: 'center' });
    
    // Organization name
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Exporterad: ${new Date().toLocaleDateString('sv-SE')}`, pageWidth / 2, 28, { align: 'center' });
    
    // Prepare data - only users with apartments, sorted by apartment number
    const tableData = sortedUsers.map(user => {
      const apt = getApartmentForUser(user.id, user.apartment_id);
      return {
        data: [
          user.name || '-',
          user.is_invoice_recipient ? `${user.email || '-'} (Faktura)` : (user.email || '-'),
          user.phone || '-',
          apt?.apartment_number || '-'
        ],
        isInvoiceRecipient: user.is_invoice_recipient
      };
    });

    // Table
    autoTable(doc, {
      startY: 35,
      head: [['Namn', 'E-post', 'Telefon', 'Lägenhet']],
      body: tableData.map(row => row.data),
      theme: 'striped',
      headStyles: {
        fillColor: [30, 41, 59],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 10
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 4
      },
      columnStyles: {
        0: { cellWidth: 45 },  // Namn
        1: { cellWidth: 75 },  // E-post - expanderad för att undvika radbrytning
        2: { cellWidth: 35 },  // Telefon
        3: { cellWidth: 25 }   // Lägenhet
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252]
      },
      margin: { top: 35, left: 10, right: 10 },
      didParseCell: (data) => {
        // Färga e-postcellen röd om det är fakturamottagare (kolumn 1)
        if (data.section === 'body' && data.column.index === 1) {
          const rowIndex = data.row.index;
          if (tableData[rowIndex]?.isInvoiceRecipient) {
            data.cell.styles.textColor = [220, 38, 38]; // red-600
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });

    // Footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128);
      doc.text(
        `Sida ${i} av ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    // Download
    doc.save(`medlemsregister-${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF exporterad');
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Kontohantering
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Hantera användare, roller och behörigheter
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportPDF} data-testid="export-pdf-button">
            <FilePdf size={18} className="mr-2" />
            Exportera PDF
          </Button>
          <Button onClick={() => handleOpenDialog()} className="btn-press" data-testid="add-user-button">
            <UserCirclePlus size={18} className="mr-2" />
            Ny användare
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="users" className="gap-2">
            <UsersIcon size={16} />
            Användare ({users.length})
          </TabsTrigger>
          <TabsTrigger value="pending" className="gap-2">
            <Clock size={16} />
            Väntande
            {pendingCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                {pendingCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="roles" className="gap-2">
            <Shield size={16} />
            Roller
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <PendingMemberUpdates onUpdateProcessed={() => { fetchData(); }} />
        </TabsContent>

        <TabsContent value="users">
          {/* Filters */}
          <div className="card-flat mb-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <MagnifyingGlass 
                  size={18} 
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" 
                />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Sök namn, e-post, telefon..."
                  className="pl-10"
                  data-testid="users-search-input"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-[180px]" data-testid="role-filter">
                  <SelectValue placeholder="Filtrera roll" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alla roller</SelectItem>
                  {Object.entries(ROLES).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="card-flat text-center">
              <div className="text-2xl font-bold text-slate-900">{users.length}</div>
              <div className="text-xs text-slate-500">Totalt</div>
            </div>
            <div className="card-flat text-center">
              <div className="text-2xl font-bold text-green-600">
                {users.filter(u => u.is_active !== false).length}
              </div>
              <div className="text-xs text-slate-500">Aktiva</div>
            </div>
            <div className="card-flat text-center">
              <div className="text-2xl font-bold text-red-600">
                {users.filter(u => u.role === 'admin').length}
              </div>
              <div className="text-xs text-slate-500">Admins</div>
            </div>
            <div className="card-flat text-center">
              <div className="text-2xl font-bold text-slate-600">
                {users.filter(u => u.role === 'resident').length}
              </div>
              <div className="text-xs text-slate-500">Boende</div>
            </div>
          </div>

          {/* User List */}
          {loading ? (
            <div className="space-y-3">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="card-flat animate-pulse">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-slate-200" />
                    <div className="flex-1">
                      <div className="h-4 w-40 bg-slate-200 rounded mb-2" />
                      <div className="h-3 w-56 bg-slate-200 rounded" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="card-flat">
              <div className="empty-state py-12">
                <User size={48} className="empty-state-icon" />
                <p className="text-slate-500">Inga användare hittades</p>
              </div>
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>
                      <button 
                        onClick={() => handleSort('name')}
                        className="flex items-center gap-1 hover:text-primary transition-colors w-full text-left"
                        data-testid="sort-name"
                      >
                        Användare {getSortIcon('name')}
                      </button>
                    </th>
                    <th>
                      <button 
                        onClick={() => handleSort('role')}
                        className="flex items-center gap-1 hover:text-primary transition-colors w-full text-left"
                        data-testid="sort-role"
                      >
                        Roll {getSortIcon('role')}
                      </button>
                    </th>
                    <th>
                      <button 
                        onClick={() => handleSort('apartment')}
                        className="flex items-center gap-1 hover:text-primary transition-colors w-full text-left"
                        data-testid="sort-apartment"
                      >
                        Lägenhet {getSortIcon('apartment')}
                      </button>
                    </th>
                    <th>
                      <button 
                        onClick={() => handleSort('status')}
                        className="flex items-center gap-1 hover:text-primary transition-colors w-full text-left"
                        data-testid="sort-status"
                      >
                        Status {getSortIcon('status')}
                      </button>
                    </th>
                    <th className="w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.map((user) => (
                    <tr key={user.id} data-testid={`user-row-${user.id}`}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            ROLES[user.role]?.color || 'bg-slate-100'
                          }`}>
                            {(() => {
                              const Icon = ROLES[user.role]?.icon || User;
                              return <Icon size={18} weight="bold" />;
                            })()}
                          </div>
                          <div>
                            <div className="font-medium text-slate-900 flex items-center gap-2">
                              {user.name || '-'}
                              {user.id === currentUser?.id && (
                                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                                  Du
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 flex items-center gap-2">
                              <span className="flex items-center gap-1" style={user.is_invoice_recipient ? { color: '#dc2626', fontWeight: 500 } : {}}>
                                <Envelope size={12} style={user.is_invoice_recipient ? { color: '#dc2626' } : {}} />
                                {user.email}
                                {user.is_invoice_recipient && (
                                  <span style={{ fontSize: '10px', backgroundColor: '#fecaca', color: '#dc2626', padding: '2px 6px', borderRadius: '4px', marginLeft: '4px', fontWeight: 600 }}>Faktura</span>
                                )}
                              </span>
                              {user.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone size={12} />
                                  {user.phone}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {canEditUser(user) ? (
                          <Select
                            value={user.role}
                            onValueChange={(newRole) => handleQuickRoleChange(user, newRole)}
                          >
                            <SelectTrigger className="h-7 w-auto gap-1 px-2 border-0 bg-transparent hover:bg-slate-100">
                              <RoleBadge role={user.role} />
                            </SelectTrigger>
                            <SelectContent>
                              {getAssignableRoles().map(role => (
                                <SelectItem key={role} value={role}>
                                  <div className="flex items-center gap-2">
                                    <RoleBadge role={role} />
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <RoleBadge role={user.role} />
                        )}
                      </td>
                      <td>
                        <div className="text-sm text-slate-600">
                          {(() => {
                            const aptLabel = getApartmentLabel(user.id, user.apartment_id);
                            return aptLabel ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded">
                                <House size={12} />
                                {aptLabel}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            );
                          })()}
                        </div>
                      </td>
                      <td><StatusBadge isActive={user.is_active !== false} /></td>
                      <td>
                        <div className="flex items-center gap-1 justify-end">
                          {canEditUser(user) && (
                            <>
                              <button
                                onClick={() => handleOpenDialog(user as UserData)}
                                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md"
                                data-testid={`edit-user-${user.id}`}
                                title="Redigera"
                              >
                                <PencilSimple size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(user)}
                                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                                data-testid={`delete-user-${user.id}`}
                                title="Ta bort"
                              >
                                <Trash size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="roles">
          <div className="space-y-4">
            <div className="card-flat">
              <h3 className="text-lg font-semibold mb-4">Rollhierarki</h3>
              <p className="text-sm text-slate-500 mb-6">
                Högre nivåer kan hantera lägre nivåer. Din nuvarande roll: <RoleBadge role={currentUser?.role || 'resident'} />
              </p>
              
              <div className="space-y-3">
                {Object.entries(ROLES)
                  .sort((a, b) => b[1].level - a[1].level)
                  .map(([key, config]) => {
                    const Icon = config.icon;
                    const count = users.filter(u => u.role === key).length;
                    const canManage = canManageRole(key);
                    
                    return (
                      <div 
                        key={key} 
                        className={`flex items-center gap-4 p-4 rounded-lg border ${
                          currentUser?.role === key ? 'border-primary bg-primary/5' : 'border-slate-200'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.color}`}>
                          <Icon size={20} weight="bold" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{config.label}</span>
                            {currentUser?.role === key && (
                              <span className="text-xs bg-primary text-white px-2 py-0.5 rounded-full">
                                Din roll
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">
                            Nivå {config.level} • {count} användare
                          </p>
                        </div>
                        <div className="text-sm text-slate-500">
                          {canManage ? (
                            <span className="text-green-600 flex items-center gap-1">
                              <CheckCircle size={16} />
                              Kan hantera
                            </span>
                          ) : (
                            <span className="text-slate-400 flex items-center gap-1">
                              <XCircle size={16} />
                              Kan ej hantera
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div className="card-flat">
              <h3 className="text-lg font-semibold mb-4">Behörigheter per roll</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3">Funktion</th>
                      <th className="text-center py-2 px-3">Superadmin</th>
                      <th className="text-center py-2 px-3">Admin</th>
                      <th className="text-center py-2 px-3">Styrelse</th>
                      <th className="text-center py-2 px-3">Boende</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {[
                      { name: 'Hantera alla föreningar', sa: true, a: false, b: false, r: false },
                      { name: 'Hantera användare', sa: true, a: true, b: false, r: false },
                      { name: 'Hantera lägenheter', sa: true, a: true, b: true, r: false },
                      { name: 'Hantera ärenden', sa: true, a: true, b: true, r: false },
                      { name: 'Skapa ärenden', sa: true, a: true, b: true, r: true },
                      { name: 'Se statistik', sa: true, a: true, b: true, r: false },
                      { name: 'Skicka meddelanden', sa: true, a: true, b: true, r: false },
                    ].map((perm) => (
                      <tr key={perm.name}>
                        <td className="py-2 px-3">{perm.name}</td>
                        <td className="text-center py-2 px-3">
                          {perm.sa ? <CheckCircle className="inline text-green-600" size={16} /> : <XCircle className="inline text-slate-300" size={16} />}
                        </td>
                        <td className="text-center py-2 px-3">
                          {perm.a ? <CheckCircle className="inline text-green-600" size={16} /> : <XCircle className="inline text-slate-300" size={16} />}
                        </td>
                        <td className="text-center py-2 px-3">
                          {perm.b ? <CheckCircle className="inline text-green-600" size={16} /> : <XCircle className="inline text-slate-300" size={16} />}
                        </td>
                        <td className="text-center py-2 px-3">
                          {perm.r ? <CheckCircle className="inline text-green-600" size={16} /> : <XCircle className="inline text-slate-300" size={16} />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editUser ? <PencilSimple size={20} /> : <UserCirclePlus size={20} />}
              {editUser ? 'Redigera användare' : 'Ny användare'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Förnamn *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="Förnamn"
                  data-testid="user-firstname-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Efternamn</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Efternamn"
                  data-testid="user-lastname-input"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>E-post *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="namn@example.com"
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="070-123 45 67"
                  data-testid="user-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Personnummer</Label>
                <Input
                  value={formData.personal_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, personal_number: e.target.value }))}
                  placeholder="YYYYMMDD-XXXX"
                  className="font-mono"
                  data-testid="user-pnr-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Roll</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getAssignableRoles().map(role => (
                      <SelectItem key={role} value={role}>
                        {ROLES[role].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Lägenhet</Label>
                <Select
                  value={formData.apartment_id || 'none'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, apartment_id: value === 'none' ? '' : value }))}
                >
                  <SelectTrigger data-testid="user-apartment-select">
                    <SelectValue placeholder="Ingen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ingen koppling</SelectItem>
                    {apartments.map((apt) => (
                      <SelectItem key={apt.id} value={apt.id}>
                        Lgh {apt.apartment_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Status</Label>
                <Select
                  value={formData.is_active ? 'active' : 'inactive'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, is_active: value === 'active' }))}
                >
                  <SelectTrigger data-testid="user-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Aktiv</SelectItem>
                    <SelectItem value="inactive">Inaktiv</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Invoice recipient checkbox */}
              <div className="col-span-2 flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <input
                  type="checkbox"
                  id="is_invoice_recipient"
                  checked={formData.is_invoice_recipient}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_invoice_recipient: e.target.checked }))}
                  className="w-5 h-5 rounded border-slate-300 text-primary focus:ring-primary"
                  data-testid="user-invoice-recipient-checkbox"
                />
                <div>
                  <Label htmlFor="is_invoice_recipient" className="cursor-pointer font-medium">
                    Fakturamottagare
                  </Label>
                  <p className="text-xs text-slate-500">
                    Skicka faktura för medlemsavgift till denna e-postadress
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                Avbryt
              </Button>
              <Button onClick={handleSave} className="flex-1" data-testid="save-user-button">
                {editUser ? 'Spara ändringar' : 'Skapa användare'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort användare?</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill ta bort <strong>{userToDelete?.name || userToDelete?.email}</strong>?
              Detta kan inte ångras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-600 hover:bg-red-700">
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
